import {
  CarouselProps as AntCarouselProps,
  Carousel as AntdCarousel,
  ConfigProvider,
  EmptyProps,
  Flex,
  Spin,
  Grid,
} from "antd";
const { useBreakpoint } = Grid;
import type { CarouselRef } from "antd/es/carousel";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { Empty } from "./Empty";

export interface CarouselProps extends AntCarouselProps {
  children: ReactNode;
  numberOfElements: number;
  slideToShowMD?: number;
  slideToShowSM?: number;
  slideToShowXS?: number;
  slideToShowLG?: number;
  slideToShowXL?: number;
  slideToShowXXL?: number;
  cardMaxWidth?: number;
  empty?: EmptyProps;
  loading?: boolean;
}

const getFixSlicesValues = (
  currentBreakpoint: string,
  slideToShowXXL?: number,
  slideToShowXL?: number,
  slideToShowLG?: number,
  slideToShowMD?: number,
  slideToShowSM?: number,
  slideToShowXS?: number,
  slidesToShow = 1
) => {
  let fixSlicesToShow: number;
  switch (currentBreakpoint) {
    case "xxl": {
      fixSlicesToShow = slideToShowXXL ?? Math.min(slidesToShow, 4);
      break;
    }
    case "xl": {
      fixSlicesToShow = slideToShowXL ?? Math.min(slidesToShow, 3);
      break;
    }
    case "lg": {
      fixSlicesToShow = slideToShowLG ?? Math.min(slidesToShow, 2);
      break;
    }
    case "md": {
      fixSlicesToShow = slideToShowMD ?? Math.min(slidesToShow, 2);
      break;
    }
    case "sm": {
      fixSlicesToShow = slideToShowSM ?? Math.min(slidesToShow, 1);
      break;
    }
    case "xs": {
      fixSlicesToShow = slideToShowXS ?? Math.min(slidesToShow, 1);
      break;
    }
    default:
      fixSlicesToShow = slidesToShow;
      break;
  }
  return { fixSlicesToShow };
};

export const Carousel = ({
  children,
  numberOfElements,
  slideToShowMD,
  slideToShowSM,
  slideToShowXS,
  slideToShowLG,
  slideToShowXL,
  slideToShowXXL,
  cardMaxWidth,
  rows = 1,
  initialSlide,
  empty,
  ...props
}: Readonly<CarouselProps>) => {
  const screens = useBreakpoint();
  const numberOfSlides = numberOfElements / ((props.slidesToShow ?? 1) * rows);
  const [ready, setReady] = useState(false);
  const carouselRef = useRef<CarouselRef | null>(null);
  const didInit = useRef(false);

  const { fixSlicesToShow } = useMemo(() => {
    const currentBreakpoint = (() => {
      if (screens.xxl) return "xxl";
      if (screens.xl) return "xl";
      if (screens.lg) return "lg";
      if (screens.md) return "md";
      if (screens.sm) return "sm";
      return "xs";
    })();
    const { fixSlicesToShow: breakpointSlides } = getFixSlicesValues(
      currentBreakpoint,
      slideToShowXXL,
      slideToShowXL,
      slideToShowLG,
      slideToShowMD,
      slideToShowSM,
      slideToShowXS,
      props.slidesToShow
    );
    // Cap slidesToShow to actual virtual columns when using rows
    const virtualColumns = Math.ceil(numberOfElements / rows);
    return { fixSlicesToShow: Math.min(breakpointSlides, virtualColumns) };
  }, [
    screens,
    slideToShowXXL,
    slideToShowXL,
    slideToShowLG,
    slideToShowMD,
    slideToShowSM,
    slideToShowXS,
    props.slidesToShow,
    numberOfElements,
    rows,
  ]);

  const fixSlicesToScroll = Math.min(
    fixSlicesToShow,
    props.slidesToScroll ?? 1
  );

  const maxWidth = cardMaxWidth
    ? `${fixSlicesToShow * cardMaxWidth}px`
    : "auto";

  useEffect(() => {
    if (didInit.current) return;
    setTimeout(() => {
      if (!carouselRef.current) return;
      didInit.current = true;
      if (numberOfSlides > 1 && initialSlide && initialSlide >= 0) {
        carouselRef.current.goTo(initialSlide, true);
      }
      setReady(true);
    });
  }, [initialSlide, numberOfSlides]);

  if (numberOfElements === 0) {
    return <Empty {...empty} />;
  }

  return props.loading ? (
    <Flex
      justify="center"
      align="center"
      style={props.style}
      data-testid="carousel-loading"
    >
      <Spin size="large" spinning />
    </Flex>
  ) : (
    <div style={{ maxWidth, margin: "0 auto", width: "100%" }}>
      <ConfigProvider
        theme={{
          components: {
            Carousel: {
              arrowSize: 30,
              dotHeight: 15,
              dotWidth: 15,
              dotActiveWidth: 15,
              dotGap: numberOfSlides > 20 ? 4 : 15,
            },
          },
        }}
      >
        <StyledCarousel
          ref={carouselRef}
          dots={numberOfSlides > 1}
          rows={rows}
          arrows
          infinite={false}
          swipeToSlide={true}
          draggable={true}
          style={{ opacity: ready ? 1 : 0, ...props.style }}
          lazyLoad="ondemand"
          {...props}
          slidesToShow={fixSlicesToShow}
          slidesToScroll={fixSlicesToScroll}
        >
          {children}
        </StyledCarousel>
      </ConfigProvider>
    </div>
  );
};

const StyledCarousel = styled(AntdCarousel)`
  .slick-prev,
  .slick-next {
    color: ${({ theme }) => theme.colorPrimary};
    opacity: 1;
  }

  .slick-prev::before,
  .slick-next::before {
    color: ${({ theme }) => theme.colorPrimary};
    opacity: 1;
  }

  .slick-dots li button {
    background-color: ${({ theme }) => theme.colorTextDescription};
    opacity: 1;
  }

  .slick-dots li.slick-active button {
    background-color: ${({ theme }) => theme.colorPrimary};
    z-index: 2;
  }
`;
